import {HTTP_INTERCEPTORS, HttpEvent, HttpErrorResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {HttpInterceptor, HttpHandler, HttpRequest} from '@angular/common/http';
import {TokenStorageService} from '../_services/token-storage.service';
import {BehaviorSubject, Observable, throwError} from 'rxjs';
import {catchError, filter, switchMap, take, tap} from 'rxjs/operators';
import {LoginService, RenewResponse, RenewToken} from '@irisims/justconsult-api';
import {LangService} from '@app/_services/lang.service';
import {Router} from '@angular/router';
import {KeepAliveService} from '@app/_services/keep-alive.service';
import { RedirectUrlService } from '@app/_services/redirect-url.service';

const TOKEN_HEADER_KEY = 'Authorization';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(private tokenService: TokenStorageService,
              private loginService: LoginService,
              private redirectService: RedirectUrlService,
              private keepAliveService: KeepAliveService,
              private langService: LangService,
              private router: Router) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    let authReq = req;
    const token = this.tokenService.getToken();
    if (token != null) {
      authReq = this.addTokenHeader(req, token);
      if (!authReq.url.includes('/api/session/keepalive') || !authReq.url.includes('/api/login/logout')) {
        this.keepAliveService.clearKeepAliveTimer();
        this.keepAliveService.innitKeepAliveTimer();
      }
    }
    return next.handle(authReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse && !authReq.url.includes('/api/login') && error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        if (error instanceof HttpErrorResponse && authReq.url.includes('/api/login/logout') && error.status === 401) {
          return this.handle401Error(authReq, next);
        }
        return throwError(error);
      }));
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const token: RenewToken = {
        token: this.tokenService.getRefreshToken()
      };
      if (token) {
        return this.loginService.renew(token).pipe(
          switchMap((renewToken: RenewResponse) => {
            this.isRefreshing = false;
            this.tokenService.saveToken(renewToken.accessToken);
            this.tokenService.updateRefreshToken(renewToken.renewToken);
            this.refreshTokenSubject.next(renewToken.accessToken);
            return next.handle(this.addTokenHeader(request, renewToken.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.redirectService.logoutRedirect();
            this.tokenService.signOut();
            this.keepAliveService.clearKeepAliveTimer();
            return throwError(err);
          })
        );
      }
    }
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const lang = this.langService.getLanguage();
    return request.clone({headers: request.headers.set(TOKEN_HEADER_KEY, token).set('language', lang).set('Access-Control-Allow-Headers', 'language')});
  }
}

export const authInterceptorProviders = [
  {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true}
];
